import Delete from "@mui/icons-material/Delete";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    Fade,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, orderBy } from "lodash";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import BSInput from "../../../common/js/components/BSInput";
import { Search, Sorty } from "../../../common/js/icons";
import { formatPhoneNumber } from "../utils/operations";
import constants from "./constants";

const StyledSortable = styled("span")({
    paddingRight: "5px",
    paddingBottom: "3px",
});

const StyledIdLink = styled(Link)({
    color: "#2C99D2",
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
});

const StyledLeftColumn = styled(TableCell)({
    paddingLeft: "0px",
    padding: 0,
});

const StyledPaperSpacing = styled(Paper)({
    padding: "10px 20px",
    fontSize: 14,
    marginBottom: "15px",
});

const StyledFilterInput = styled("div")({
    height: "70px",
    marginBottom: "0px", // Fixed typo from "maringBottom"
});

const StyledShrinkButton = styled(Button)({
    padding: 0,
    minWidth: 0,
    minHeight: 0,
    margin: "0 18px 0 0",
    color: "#9e9e9e",
    "&:hover": {
        backgroundColor: "transparent",
    },
});

const StyledDivider = styled(Divider)({
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    margin: "0 -20px 0 -20px",
});

const StyledLoadingPlaceholder = styled("div")({
    marginTop: "3em",
    marginBottom: "1.5em",
    textAlign: "center",
    width: "100%",
});

const StyledDialogBox = styled(Dialog)({
    width: "20em",
});

const StyledAlternateContent = styled("div")({
    margin: "auto",
    textAlign: "center",
    marginTop: "10em",
    marginBottom: "3em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
});

const StyledNoData = styled(TableCell)({
    borderBottom: "none",
});

const CrmContacts = (props) => {
    const {
        contacts,
        columnToSort,
        deleteContact,
        elementsSaving,
        filterQuery,
        handleFilter,
        handleSort,
        sortDirection,
    } = props;

    const [confirmDelete, setConfirmDelete] = useState({
        open: false,
        contactId: null,
        contactIndex: null,
    });

    const handleDeleteContact = () => {
        deleteContact(confirmDelete.contactId, confirmDelete.contactIndex);
        handleClose();
    };

    const handleOpen = (contactId, contactIndex) => {
        setConfirmDelete((prevConfirmDelete) => ({
            ...prevConfirmDelete,
            open: true,
            contactId: contactId,
            contactIndex: contactIndex,
        }));
    };

    const handleClose = () => {
        setConfirmDelete((prevConfirmDelete) => ({
            ...prevConfirmDelete,
            open: false,
            contactId: null,
            contactIndex: null,
        }));
    };

    const data = orderBy(
        filterQuery && contacts
            ? contacts.filter((x) => constants.filterByQuery(x, "contacts", filterQuery))
            : contacts,
        columnToSort,
        sortDirection
    );

    return (
        <StyledPaperSpacing>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <StyledFilterInput>
                    <BSInput
                        extraInputStyles={{ backgroundColor: "#fafafa", border: "none", marginTop: "10px" }}
                        value={filterQuery}
                        placeholder="Search contacts..."
                        startAdornment={<Search />}
                        startAdornmentStyles={{ color: "#777777" }}
                        onChange={(event) => {
                            handleFilter(event.target.value);
                        }}
                    />
                </StyledFilterInput>
            </div>
            {elementsSaving.crmContacts ? (
                <StyledLoadingPlaceholder>
                    <CircularProgress color="primary" />
                </StyledLoadingPlaceholder>
            ) : (
                <React.Fragment>
                    <StyledDivider />
                    <Fade in={true}>
                        <Table>
                            <TableHead>
                                <TableRow key={`crmContact-headers`}>
                                    <TableCell style={{ width: "20%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center" }}
                                            onClick={() => handleSort("contactFirstName")}
                                        >
                                            <StyledSortable>Name</StyledSortable>
                                            <div>
                                                <Sorty />
                                            </div>
                                        </div>
                                    </TableCell>

                                    <TableCell style={{ width: "35%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center" }}
                                            onClick={() => handleSort("contactCompanyDisplayName")}
                                        >
                                            <StyledSortable>Company</StyledSortable>
                                            <div>
                                                <Sorty />
                                            </div>
                                        </div>
                                    </TableCell>

                                    <TableCell align="right" style={{ width: "20%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center", position: "relative" }}
                                            onClick={() => handleSort("contactPhoneNumber")}
                                        >
                                            <StyledSortable>Phone Number</StyledSortable>
                                        </div>
                                    </TableCell>

                                    <TableCell align="right" style={{ width: "15%", padding: 0 }}>
                                        <div
                                            style={{ display: "flex", alignItems: "center", position: "relative" }}
                                            onClick={() => handleSort("contactEmailAddress")}
                                        >
                                            <StyledSortable>Email</StyledSortable>
                                        </div>
                                    </TableCell>

                                    <TableCell style={{ width: "10%", padding: 0 }}></TableCell>
                                </TableRow>
                            </TableHead>
                            {contacts && contacts.length > 0 ? (
                                <TableBody>
                                    {data.map((contact, index) => (
                                        <TableRow
                                            key={`crmcontact-row-${index}`}
                                            selectable="false"
                                            className="tableRow"
                                        >
                                            <StyledLeftColumn>
                                                <StyledIdLink to={`/contacts/${get(contact, "contactKey", "")}`}>
                                                    <div>{`${get(contact, "contactFirstName", "")} ${get(
                                                        contact,
                                                        "contactLastName",
                                                        ""
                                                    )}`}</div>
                                                </StyledIdLink>
                                            </StyledLeftColumn>

                                            <TableCell style={{ padding: 0 }}>
                                                {get(contact, "contactCompanyDisplayName", "")}
                                            </TableCell>

                                            <TableCell style={{ padding: 0 }}>
                                                {formatPhoneNumber(get(contact, "contactPhoneNumber", ""))}
                                            </TableCell>

                                            <TableCell style={{ padding: 0 }}>
                                                {get(contact, "contactEmailAddress", "")}
                                            </TableCell>

                                            <TableCell align="right" style={{ padding: 0 }}>
                                                <StyledShrinkButton
                                                    id={`delete-crmcontact-${index}`}
                                                    onClick={() => handleOpen(get(contact, "contactKey"), index)}
                                                >
                                                    <Delete />
                                                </StyledShrinkButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            ) : (
                                <TableBody>
                                    <TableRow>
                                        <StyledNoData colSpan={6}>
                                            <StyledAlternateContent>No Contacts</StyledAlternateContent>
                                        </StyledNoData>
                                    </TableRow>
                                </TableBody>
                            )}
                        </Table>
                    </Fade>
                </React.Fragment>
            )}
            <StyledDialogBox
                open={confirmDelete.open}
                onClose={handleClose}
                maxWidth="xs"
                aria-labelledby="confirmation-dialog-crm-contact"
            >
                <DialogContent>Delete this contact?</DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteContact} color="primary" variant="contained">
                        Ok
                    </Button>
                </DialogActions>
            </StyledDialogBox>
        </StyledPaperSpacing>
    );
};

export default CrmContacts;
